// Self-contained activity-heatmap widget. Composes the entity-level
// HeatmapGrid primitive with a section header + low/high legend swatches.
// Receives the raw cells from the page; renders them as a 24×7 grid with
// opacity scaled to the busiest cell.

import type { ContentHeatmapCell } from "@community-analytics/shared";

import { HeatmapGrid } from "@entities/message";
import { cn } from "@shared/lib";

import { ACTIVITY_HEATMAP_STRINGS } from "../lib/strings";

export interface ActivityHeatmapProps {
  cells: ReadonlyArray<ContentHeatmapCell>;
  className?: string;
}

export function ActivityHeatmap({ cells, className }: ActivityHeatmapProps) {
  return (
    <section
      aria-labelledby="activity-heatmap-title"
      className={cn("flex flex-col gap-2", className)}
    >
      <header>
        <h2 id="activity-heatmap-title" className="text-sm font-semibold">
          {ACTIVITY_HEATMAP_STRINGS.title}
        </h2>
        <p className="text-xs text-muted-foreground">
          {ACTIVITY_HEATMAP_STRINGS.subtitle}
        </p>
      </header>
      <HeatmapGrid
        cells={cells}
        dayLabels={ACTIVITY_HEATMAP_STRINGS.dayLabels}
        hourLabel={ACTIVITY_HEATMAP_STRINGS.hourTick}
        cellLabel={({ dayOfWeek, hour, messages }) =>
          ACTIVITY_HEATMAP_STRINGS.cellTooltip({
            dayLabel:
              ACTIVITY_HEATMAP_STRINGS.dayLabels[dayOfWeek] ?? "?",
            hour,
            messages,
          })
        }
        emptyLabel={ACTIVITY_HEATMAP_STRINGS.empty}
      />
      <div className="flex items-center gap-2 self-end text-[10px] text-muted-foreground">
        <span>{ACTIVITY_HEATMAP_STRINGS.legendLow}</span>
        <span
          aria-hidden="true"
          className="h-2 w-3 rounded-sm bg-primary"
          style={{ opacity: 0.1 }}
        />
        <span
          aria-hidden="true"
          className="h-2 w-3 rounded-sm bg-primary"
          style={{ opacity: 0.4 }}
        />
        <span
          aria-hidden="true"
          className="h-2 w-3 rounded-sm bg-primary"
          style={{ opacity: 0.7 }}
        />
        <span
          aria-hidden="true"
          className="h-2 w-3 rounded-sm bg-primary"
          style={{ opacity: 0.95 }}
        />
        <span>{ACTIVITY_HEATMAP_STRINGS.legendHigh}</span>
      </div>
    </section>
  );
}
